import React from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getCollborationAgreementDownloadLink, LicenseCollaborativeAgreement } from '@uplift/api';
import { DateFormats } from '@uplift/constants';
import { useTimezone } from '@uplift/contexts';
import { CalendarDateService } from '@uplift/services';

import { Button, LightChip, Loader, Modal, ModalActions, ModalContent } from '@uplift/ui-v2';
import { PDFPage, usePDFDocument } from '@uplift/pdf-viewer';
import { collaborativeAgreementRejectionReasonText } from '@uplift/pages';

export type ViewCollabAgreementDocumentModalProps = {
  agreement: LicenseCollaborativeAgreement;
  providerId: string;
  stateName?: string;
  onUploadNew?: () => void;
  onClose: () => void;
};

export const ViewCollaborativeAgreementDocumentModal: React.VFC<ViewCollabAgreementDocumentModalProps> = ({
  agreement,
  providerId,
  stateName,
  onUploadNew,
  onClose,
}) => {
  const timezone = useTimezone();

  const { data: downloadLink } = useQuery({
    queryKey: [ 'collabAgreementDownloadLink', providerId, agreement.id ],
    queryFn: () => getCollborationAgreementDownloadLink(providerId, agreement.id),
    select: (res) => res.data.downloadLink,
  });
  const { data: pdf } = usePDFDocument(downloadLink);
  const pages = React.useMemo(() => Array.from({ length: pdf?.numPages ?? 0 }).map((_, index) => index), [ pdf ]);

  const isRejected = agreement.rejectedAt !== null;
  const rejectedDate = agreement.rejectedAt ? CalendarDateService.date(agreement.rejectedAt).tz(timezone) : null;
  const uploadedDate = CalendarDateService.date(agreement.uploadedAt).tz(timezone);

  return (
    <Modal
      open
      title={`${stateName ? `${stateName} ` : ''}Collaborative Agreement`}
      maxWidth="xl"
      fullWidth
      showCloseIcon
      onClose={onClose}
    >
      <ModalContent>
        <Box display="flex" height="70vh" gap={0} overflow="hidden">
          <Box flex={1} overflow="auto" bgcolor="grey.100" p={2}>
            {pages.length > 0 ? (
              <Box display="flex" flexDirection="column" gap={2}>
                {pages.map((p) => (
                  <div key={p}>
                    {pdf && <PDFPage pdf={pdf} pageNumber={p} zoomAndDrag />}
                  </div>
                ))}
              </Box>
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <Loader />
              </Box>
            )}
          </Box>

          <Divider orientation="vertical" />

          <Box width={300} flexShrink={0} overflow="auto" p={3}>
            <Box display="flex" flexDirection="column" gap={2.5}>
              <div>
                <LightChip
                  size="small"
                  color={isRejected ? 'error' : 'default'}
                  label={isRejected ? 'Rejected' : 'In Review'}
                />
              </div>

              {isRejected && (
                <Box sx={{ bgcolor: '#fdeded', border: 1, borderColor: 'error.light', borderRadius: 1, p: 1.5 }}>
                  <Typography variant="caption" sx={{ color: 'error.main' }} display="block" fontWeight={600}>
                    Rejected{rejectedDate ? ` on ${rejectedDate.format(DateFormats.DEFAULT_DATE)}` : ''}
                  </Typography>
                  <Typography variant="body2" color="textPrimary" mt={0.5}>
                    {collaborativeAgreementRejectionReasonText(agreement.rejectionReason, agreement.rejectionDetails)}
                  </Typography>
                  {agreement.rejectionReason !== 'other' && agreement.rejectionDetails && (
                    <Typography variant="body2" color="textSecondary" mt={0.5}>
                      {agreement.rejectionDetails}
                    </Typography>
                  )}
                  <Typography variant="caption" color="textSecondary" display="block" mt={1}>
                    Please upload a new agreement to resubmit for review.
                  </Typography>
                </Box>
              )}

              <Box>
                <Typography variant="caption" display="block">
                  Uploaded on {uploadedDate.format(DateFormats.DEFAULT_DATE)} at {uploadedDate.format(DateFormats['hh:mm A'])}
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Typography variant="caption" color="textSecondary" display="block">Expiration Date</Typography>
                <Typography variant="body2" color="textPrimary">
                  {agreement.expirationDate
                    ? CalendarDateService.date(agreement.expirationDate, 'YYYY-MM-DD').format(DateFormats.DEFAULT_DATE)
                    : 'No expiration date'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary" display="block">Supervising Physician</Typography>
                <Typography variant="body2" color="textPrimary">
                  {agreement.physicianFirstName} {agreement.physicianLastName}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary" display="block">Supervisor NPI</Typography>
                <Typography variant="body2" color="textPrimary">{agreement.physicianNpi}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </ModalContent>
      <ModalActions>
        <Button variant="outlined" color="secondary" onClick={onClose}>Close</Button>
        {isRejected && onUploadNew && (
          <Button variant="contained" color="secondary" onClick={onUploadNew}>Upload New</Button>
        )}
      </ModalActions>
    </Modal>
  );
};
